/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment } from 'react';

import type { Header } from '@tanstack/react-table';

import { IconButton } from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';

import { SortIcon } from '../sort-icon';

interface HeaderSortButtonProps<T extends object> {
	charlieTableColumn: Header<T, unknown>;
	showSortButton: boolean;
	sortDirection: false | 'asc' | 'desc';
	sortLabel?: string | ((header: Header<T, unknown>) => string);
	onSortButtonFocusChange: (focused: boolean) => void;
	onToggleSort: () => void;
	interactionName?: string;
	analyticsContext?: Record<string, unknown>;
}

/**
 * Sort button and icon for table header. Shown when column is sortable and (hovered, focused, or currently sorted).
 */
export const HeaderSortButton = <T extends object>({
	charlieTableColumn,
	showSortButton,
	sortDirection,
	sortLabel,
	onSortButtonFocusChange,
	onToggleSort,
	interactionName,
	analyticsContext,
}: HeaderSortButtonProps<T>) => {
	const label =
		sortLabel != null
			? typeof sortLabel === 'function'
				? sortLabel(charlieTableColumn)
				: sortLabel
			: '';

	return (
		<IconButton
			testId={`${charlieTableColumn.id}-sort-button`}
			spacing="compact"
			appearance="subtle"
			onClick={onToggleSort}
			interactionName={interactionName}
			analyticsContext={{
				componentName: 'charlieTableSortColumnButton',
				...analyticsContext,
			}}
			isTooltipDisabled={!label}
			label={label}
			onFocus={() => onSortButtonFocusChange(true)}
			onBlur={() => onSortButtonFocusChange(false)}
			icon={(iconProps) => (
				<Fragment>
					{(showSortButton || sortDirection) && (
						<SortIcon
							{...iconProps}
							sortDirection={sortDirection ?? 'asc'}
							isCurrentSort={Boolean(sortDirection)}
						/>
					)}
				</Fragment>
			)}
		/>
	);
};
